import React from 'react';
import { ArrowRight, CheckCircle, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { comparisonRows, solutions as fallbackSolutions, whyReasons } from '../data/siteData';
import { getContentIcon, useCollectionData } from '../services/contentApi';
import './Solutions.css';

const ComparisonValue = ({ value }) => {
  if (value === true) {
    return (
      <span className="compare-value positive">
        <CheckCircle size={17} aria-hidden="true" />
        Yes
      </span>
    );
  }

  if (value === false) {
    return (
      <span className="compare-value muted">
        <X size={16} aria-hidden="true" />
        No
      </span>
    );
  }

  return <span className="compare-value partial">{value}</span>;
};

const Solutions = () => {
  const { items: solutions } = useCollectionData('solutions', fallbackSolutions);

  return (
  <>
    <section className="page-hero solutions-hero">
      <div className="container page-hero-grid">
        <div>
          <p className="eyebrow">Facility solutions</p>
          <h1 className="page-title">Linen supply built around each care setting.</h1>
        </div>
        <p className="page-kicker">
          From major health systems to specialty clinics, NovaCareLinens tailors
          inventory and delivery schedules to meet your specific utilization rates.
        </p>
      </div>
    </section>

    <section className="section facility-section">
      <div className="container facility-grid">
        {solutions.map((solution, index) => {
          const { title, description, tag, icon, tone } = solution;
          const Icon = getContentIcon(icon);
          const features = Array.isArray(solution.features) ? solution.features : [tag].filter(Boolean);
          return (
            <article className="facility-card surface-card stagger-card" key={title || index} style={{ '--delay': `${index * 90}ms` }}>
              <span className={`icon-circle tone-${tone || 'blue'}`}>
                <Icon size={25} strokeWidth={1.8} aria-hidden="true" />
              </span>
              <h2>{title}</h2>
              <p>{description || solution.desc}</p>
              <ul className="check-list">
                {features.map((feature) => (
                  <li key={feature}>
                    <CheckCircle size={16} aria-hidden="true" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Link className="text-link" to="/contact">
                Talk to procurement <ArrowRight size={17} aria-hidden="true" />
              </Link>
            </article>
          );
        })}
      </div>
    </section>

    <section className="section procurement-section" id="procurement">
      <div className="container">
        <div className="section-header">
          <div>
            <p className="eyebrow">Procurement model</p>
            <h2 className="section-title">The smarter choice for healthcare procurement.</h2>
          </div>
          <p className="section-sub">
            We combine healthcare-only specialization, fast fulfillment,
            compliance support, and account-level ordering tools for facilities
            that need reliability without a bloated supplier process.
          </p>
        </div>

        <div className="procurement-grid">
          {whyReasons.map(({ title, desc, Icon }, index) => (
            <article className="procurement-card stagger-card" key={title} style={{ '--delay': `${index * 55}ms` }}>
              <span className="icon-circle tone-teal">
                <Icon size={22} strokeWidth={1.8} aria-hidden="true" />
              </span>
              <h3>{title}</h3>
              <p>{desc}</p>
            </article>
          ))}
        </div>

        <div className="comparison-card surface-card">
          <div className="comparison-title">
            <p className="eyebrow">Supplier comparison</p>
            <h2>NovaCareLinens vs. traditional suppliers</h2>
          </div>
          <div className="comparison-table" role="table" aria-label="NovaCareLinens compared with general suppliers">
            <div className="comparison-row comparison-head" role="row">
              <span role="columnheader">Capability</span>
              <span role="columnheader">NovaCareLinens</span>
              <span role="columnheader">General suppliers</span>
            </div>
            {comparisonRows.map(([label, us, them]) => (
              <div className="comparison-row" role="row" key={label}>
                <span role="cell">{label}</span>
                <span role="cell"><ComparisonValue value={us} /></span>
                <span role="cell"><ComparisonValue value={them} /></span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  </>
  );
};

export default Solutions;
